<template>
	<div class="dropzone-container" :class="{'ai-dropzone':isAi, multiple: maxFiles > 1, disabled: !ready}" :style="computedStyle" :data-files-count="currentFiles.length">
		<p class="txt-input-label" v-if="label">{{ label }}</p>
		<div class="dropzone-wrapper" :class="{filled: currentFiles.length}">
			<div ref="dropzone" class="dropzone">
				<div @click="clickDeleteBtn" ref="delete-btn" class="delete-btn"></div>
			</div>
			<div class="row summary-row">
				<div ref="summary" class="summary-extended-container">
					<div
						v-for="(file, index) in currentFiles"
						:key="index"
						class="summary-img">
						<div @click="clickDeleteBtn" :data-url="file" class="delete-img"></div>
						<img :ref="'img-'+index" :src="file" draggable="false" :alt="'file '+index">
					</div>
				</div>
			</div>
		</div>
		<AppPopup
			:title="getString('uploading')"
			:bodyText="popupBodyText"
			ref="popup"
			:buttons="[]"
			prevent-overlay-dismiss
		>
		</AppPopup>
	</div>
</template>

<script>
import AppPopup from "@/shared/components/AppPopup.vue";
import {useLanguageStore, useScriptStore, useNotificationStore} from "@/shared/stores";
import {uploadImageFile} from "@/shared/helpers/uploadS3";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const scriptStore = useScriptStore();
		const notificationStore = useNotificationStore();
		return {getString: languageStore.getString, loadScriptsSequence: scriptStore.loadScriptsSequence, showNotification: notificationStore.showNotification};
	},
	emits: ["uploading", "upload-complete", "removed-file"],
	name: "AppDropzone",
	components: {AppPopup},
	data() {
		return {
			dropzoneObj: null,
			options: null,
			currentFiles: [],
			popupBodyText: "",
			ready: false,
			allowedFileTypes: this.allowPdf ? ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'] : ['image/jpeg', 'image/jpg', 'image/png']
		}
	},
	props: {
		label: {type: String, default: null},
		maxFiles: {type: Number, default: 1},
		//Compression bytes size
		maxBytes: {type: Number, default: 1024 * 600},
		maxWidth: {type: Number, default: 1024},
		maxHeight: {type: Number, default: 1024},
		files: {type: Array, default: []},
		createThumbnail: {type: Boolean, default: false},
		allowPdf: {type: Boolean, default: false},
		isAi: {type: Boolean, default: false},
	},
	methods: {
		initDropzone() {
			if(!this.dropzoneObj && this.$refs.dropzone && !this.$refs.dropzone.classList.contains("dz-clickable")) {
				this.initDropzoneSettings();
				this.initDropzoneListeners();
				this.currentFiles = this.files;
			} else {
				console.warn("Dropzone already initialized");
			}
		},
		initDropzoneSettings() {
			this.options = {
				url: "#",
				dictDefaultMessage: "",
				acceptedFiles: this.allowPdf ? "image/jpeg, image/jpg, image/png, application/pdf" : "image/jpeg, image/jpg, image/png",
				maxFiles: this.maxFiles,
				maxFilesize: Infinity,
				autoProcessQueue: false,
				autoDiscover: false
			};
			this.dropzoneObj = new Dropzone(this.$refs.dropzone, this.options);
		},
		initDropzoneListeners() {
			if(this.dropzoneObj) {
				this.dropzoneObj.on("addedfile", this.handleSingleAddFile);
				this.dropzoneObj.on("addedfiles", this.handleAddFilesAction)
			}
		},
		handleSingleAddFile(file) {
			if (this.dropzoneObj.files.length > this.options.maxFiles) {
				this.dropzoneObj.removeFile(file);
				for (let i = 0; i < this.dropzoneObj.files.length; i++) {
					if (i >= this.options.maxFiles || !this.allowedFileTypes.includes(this.dropzoneObj.files[i].type)) {
						this.dropzoneObj.removeFile(this.dropzoneObj.files[i]);
					}
				}
			} else if (!this.allowedFileTypes.includes(file.type) && !(this.allowPdf && file.type === 'application/pdf')) {
				this.showNotification('file-type-not-supported', [this.allowedFileTypes.join(', ')]);
				this.dropzoneObj.removeFile(file);
			}
		},
		handleAddFilesAction(files) {
			let acceptedArray = Object.assign([], Array.prototype.slice.call(files).filter(file => file.accepted && !file.error));
			if (acceptedArray.length > 0) {

				this.$emit("uploading", acceptedArray);
				this.filesUploadPopup(acceptedArray, (newUrls) => {
					if (newUrls && newUrls.length) {
					this.currentFiles = this.maxFiles === 1 ? [newUrls[0].url] : this.currentFiles.concat(newUrls.map(item => item.url));
						this.$emit("upload-complete", this.currentFiles, files);
					}
				});
			}
		},
		filesUploadPopup(files, callback) {

			this.$refs.popup.toggleStatus(true);

			let x = 0;
			let uploadedURLs = [];
			const popup = this.$refs.popup;
			let loopArray = files => {
				this.popupBodyText = this.getString('wait_for_upload', [(x + 1) + "/" + files.length]);

				if (this.allowPdf && files[x].type === 'application/pdf') {
					// Skip compression for PDF files
					uploadImageFile(files[x], 0, 0, 0, false, name => {
						let bucketURL = "https://img.qromo.io";
						uploadedURLs.push({id: files[x], url: bucketURL + "/businesses/" + name});
						x++;
						if (x < files.length) {
							loopArray(files);
						} else {
							this.popupBodyText = this.getString('upload_complete');
							setTimeout(() => {
								popup.toggleStatus(false)
							}, 500);
							callback(uploadedURLs);
						}
					}, () => {
						this.popupBodyText = this.getString('upload_error');
						this.dropzoneObj.removeAllFiles();
						setTimeout(() => {
							popup.toggleStatus(false)
						}, 3500);
					});
				} else {
					uploadImageFile(files[x], this.maxWidth, this.maxHeight, this.maxBytes, this.createThumbnail, name => {
						let bucketURL = "https://img.qromo.io";
						uploadedURLs.push({id: files[x], url: bucketURL + "/businesses/" + name});
						x++;
						if (x < files.length) {
							loopArray(files);
						} else {
							this.popupBodyText = this.getString('upload_complete');
							setTimeout(() => {
								popup.toggleStatus(false)
							}, 500);
							callback(uploadedURLs);
						}
					}, () => {
						this.popupBodyText = this.getString('upload_error');
						this.dropzoneObj.removeAllFiles();
						setTimeout(() => {
							popup.toggleStatus(false)
						}, 3500);
					});
				}
			}
			loopArray(files);
		},
		clickDeleteBtn(e) {
			this.dropzoneObj.removeAllFiles();
			let url = e.currentTarget.dataset.url;
			if (this.maxFiles === 1) {
				this.currentFiles = [];
			} else {
				let urlIndex = this.currentFiles.indexOf(url);
				this.currentFiles.splice(urlIndex, 1);
			}
			this.$emit("removed-file", url, this.currentFiles);
		}
	},
	computed: {
		computedStyle() {
			return this.maxFiles === 1 && this.currentFiles.length ? '--image-url-loaded: url(' + this.currentFiles[0] + ')' : '';
		},
	},
	mounted() {
		this.ready = false;
		this.loadScriptsSequence(["amazonSdk", "imageCompressor", "dropzone"], () => {
			this.$nextTick(() => {
				if(typeof Dropzone === 'undefined') {
					setTimeout(() => {
						if(typeof Dropzone === 'undefined') {
							console.error("Dropzone not loaded");
						} else {
							this.ready = true;
							this.initDropzone();
						}
					}, 1000);
				} else {
					this.ready = true;
					this.initDropzone();
				}
			});
		});
	},
	beforeUnmount() {
		if(this.dropzoneObj && this.dropzoneObj.destroy) {
			this.dropzoneObj.destroy();
		}
	},
	watch: {
		files(newFiles) {
			this.currentFiles = newFiles;
			// If files array is cleared externally, clear the dropzone object too
			if (newFiles.length === 0 && this.dropzoneObj) {
				this.dropzoneObj.removeAllFiles();
			}
		}
	}
}
</script>

<style lang="scss" scoped>

.dropzone-container {
	width: 80px;

	.txt-input-label {
		margin-bottom: 5px;
	}

	.dropzone {
		position: relative;
		width: 80px;
		height: 80px;
		min-height: 80px;
		border: none;
		border-radius: 13px;
		padding: 10px;
		cursor: pointer;

		.delete-btn {
			top: 5px;
			right: 5px;
		}
	}

	&:deep(.delete-img), .delete-img, .delete-btn {
		display: none;
		position: absolute;
		height: 8px;
		width: 8px;
		background-size: contain;
		background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12.828' height='12.828' viewBox='0 0 12.828 12.828'%3E%3Cg transform='translate(1.414 1.414)'%3E%3Cpath d='M6.407,6.368l9.978,10' transform='translate(-6.396 -6.368)' fill='none' stroke='%23b1b1b1' stroke-linecap='round' stroke-width='2'/%3E%3Cpath d='M0,0,9.978,10' transform='translate(0 9.989) rotate(-90)' fill='none' stroke='%23b1b1b1' stroke-linecap='round' stroke-width='2'/%3E%3C/g%3E%3C/svg%3E%0A");
		background-repeat: no-repeat;
		cursor: pointer;
	}

	.dropzone-wrapper {
		border-radius: 13px;
		box-shadow: 0 1px 2px $alpha_4;
		background: var(--color-bg);
	}

	&.ai-dropzone {
		width: 150px;

		.dropzone-wrapper {
			background: #8f4dfe0d;
			position: relative;

			&::before{
				content: "";
				position: absolute;
				inset: 0;
				padding: 2px;
				border-radius: inherit;
				--angle:120deg;
				background: linear-gradient(var(--angle),var(--color-secondary) 0%,#E3216B 27%,#8F4DFE 63%,var(--color-primary) 100%);
				-webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
				-webkit-mask-composite: xor;
				mask-composite: exclude;
				pointer-events: none;
				transition: --angle .6s linear;
			}

		}
		.dropzone {
			width: 150px;
			height: 150px;
			min-height: 150px;
			padding: 16px;
		}


		&:deep(.dz-message) {
			border: 2px dashed #818181;
			&:before {
				background-image: url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2261%22%20height%3D%2260%22%20viewBox%3D%220%200%2061%2060%22%20fill%3D%22none%22%3E%3Cscript%20xmlns%3D%22%22%20id%3D%22eppiocemhmnlbhjplcgkofciiegomcon%22%2F%3E%3Cscript%20xmlns%3D%22%22%2F%3E%3Cscript%20xmlns%3D%22%22%2F%3E%3Cpath%20d%3D%22M54.5479%200H6.38962C4.56152%200.154077%202.86836%201.01063%201.67607%202.38455C0.483768%203.75847-0.111917%205.53941%200.0177461%207.3425V52.6538C-0.112961%2054.4575%200.482249%2056.2394%201.67465%2057.6141C2.86705%2058.9888%204.56081%2059.8459%206.38962%2060H54.5479C56.3767%2059.8459%2058.0704%2058.9888%2059.2628%2057.6141C60.4552%2056.2394%2061.0505%2054.4575%2060.9197%2052.6538V7.3425C61.0494%205.53941%2060.4537%203.75847%2059.2614%202.38455C58.0691%201.01063%2056.376%200.154077%2054.5479%200ZM4.26566%2052.6538V7.3425C4.26566%205.6625%205.25912%204.18125%206.38962%204.18125H54.5479C55.6898%204.18125%2056.6718%205.65875%2056.6718%207.3425V41.55L47.0036%2029.7675C46.0614%2028.62%2044.8592%2027.7062%2043.4936%2027.0992C42.128%2026.4923%2040.637%2026.2092%2039.1403%2026.2726C37.6435%2026.3361%2036.1827%2026.7443%2034.8751%2027.4646C33.5674%2028.1848%2032.4494%2029.197%2031.6107%2030.42L14.2003%2055.815H6.38962C5.24771%2055.815%204.26566%2054.3375%204.26566%2052.6538ZM54.5441%2055.815H19.3237L35.1201%2032.7638C35.585%2032.0798%2036.2066%2031.5131%2036.9347%2031.1095C37.6628%2030.7058%2038.4771%2030.4764%2039.3118%2030.4399C40.1465%2030.4034%2040.9781%2030.5608%2041.7397%2030.8993C42.5013%2031.2379%2043.1714%2031.7481%2043.6959%2032.3888L56.6376%2048.1387L56.6756%2048.1688V52.6312C56.6756%2054.3112%2055.686%2055.7925%2054.5517%2055.7925%22%20fill%3D%22%23E3216B%22%2F%3E%3Cpath%20d%3D%22M26.4425%2023.08C26.8725%2023.27%2026.8725%2023.88%2026.4425%2024.06C23.9425%2025.14%2021.9525%2027.13%2020.8725%2029.63C20.6825%2030.06%2020.0725%2030.06%2019.8925%2029.63C18.8125%2027.13%2016.8225%2025.14%2014.3225%2024.06C13.8925%2023.87%2013.8925%2023.26%2014.3225%2023.08C16.8225%2022%2018.8125%2020.01%2019.8925%2017.51C20.0825%2017.08%2020.6925%2017.08%2020.8725%2017.51C21.9525%2020.01%2023.9425%2022%2026.4425%2023.08Z%22%20fill%3D%22%23E3216B%22%2F%3E%3Cpath%20d%3D%22M30.9725%2016.08C31.2125%2016.18%2031.2125%2016.52%2030.9725%2016.63C29.5825%2017.23%2028.4725%2018.34%2027.8625%2019.74C27.7625%2019.98%2027.4225%2019.98%2027.3125%2019.74C26.7125%2018.35%2025.6025%2017.24%2024.2025%2016.63C23.9625%2016.53%2023.9625%2016.19%2024.2025%2016.08C25.5925%2015.48%2026.7025%2014.37%2027.3125%2012.97C27.4125%2012.73%2027.7525%2012.73%2027.8625%2012.97C28.4625%2014.36%2029.5725%2015.47%2030.9725%2016.08Z%22%20fill%3D%22%23E3216B%22%2F%3E%3Cpath%20d%3D%22M20.0125%2012.3L20.6925%2012.6C20.8125%2012.65%2020.8125%2012.82%2020.6925%2012.87L20.0125%2013.17C19.3225%2013.47%2018.7725%2014.02%2018.4825%2014.7L18.1825%2015.38C18.1325%2015.5%2017.9625%2015.5%2017.9125%2015.38L17.6125%2014.7C17.3125%2014.01%2016.7625%2013.46%2016.0825%2013.17L15.4025%2012.87C15.2825%2012.82%2015.2825%2012.65%2015.4025%2012.6L16.0825%2012.3C16.7725%2012%2017.3225%2011.45%2017.6125%2010.77L17.9125%2010.09C17.9625%209.97%2018.1325%209.97%2018.1825%2010.09L18.4825%2010.77C18.7825%2011.46%2019.3325%2012.01%2020.0125%2012.3Z%22%20fill%3D%22%23E3216B%22%2F%3E%3C%2Fsvg%3E");
			}
		}
	}

	&.multiple {
		width: 300px;

		.dropzone, .dropzone-wrapper {
			width: 100%;
		}

		.summary-row {
			@include alignFlex(center);
			width: 100%;
			overflow: hidden;
			overflow-x: auto;
			height: 0;
			background-color: #DFE9F3;
			transition: height .3s $transition;
			border-radius: 0 0 13px 13px;
			flex-wrap: nowrap;

			&::-webkit-scrollbar {
				width: 8px;
				height: 8px;
				background-color: transparent;
			}

			&::-webkit-scrollbar-thumb {
				background-color: $txt_1;
				border-radius: 10px;
				width: 8px;
				height: 8px;
			}

			scrollbar-width: thin;
			scrollbar-color: $txt_1;

			.summary-extended-container {
				@include row;
				max-width: unset;
				flex-wrap: nowrap;
				padding-top: 6px;
			}

			&:deep(.summary-img) {
				@include centered;
				height: 50px;
				width: 50px;
				position: relative;
				border-radius: 13px;
				border: 1px solid $primary;
				margin: 0 12px;

				.delete-img {
					border-radius: 13px;
					top: -4px;
					right: -7px;
				}

				img {
					max-width: 100%;
					max-height: 100%;
					padding: 2px;
				}
			}

			&:hover:deep(.summary-img) .delete-img {
				display: block;
			}

			@media (max-width: 1080px) {
				&:deep(.summary-img) .delete-img {
					display: block;
				}
			}
		}

		.dropzone-wrapper.filled .summary-row {
			height: 80px;
		}
	}

	&:deep(.dz-button), &:deep(.dz-preview), &:deep(.dz-image-preview) {
		display: none;
	}

	&:deep(.dz-message) {
		border: 2px dashed $txt_0;
		border-radius: 13px;
		height: 100%;
		width: 100%;
		position: relative;

		&:before, &:after {
			content: '';
			position: absolute;
			top: 5px;
			left: 5px;
			width: calc(100% - 10px);
			height: calc(100% - 10px);
			background-repeat: no-repeat;
			background-position: center;
			border-radius: 13px;
		}

		&:before {
			display: block;
			background-image: url("data:image/svg+xml,%3Csvg id='icon-image' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' width='30' height='29.999' viewBox='0 0 30 29.999'%3E%3Cdefs%3E%3CclipPath id='clip-path'%3E%3Crect width='30' height='29.999' fill='%23b1b1b1'/%3E%3C/clipPath%3E%3C/defs%3E%3Cg clip-path='url(%23clip-path)'%3E%3Cpath d='M26.86,0H3.14A3.441,3.441,0,0,0,0,3.672V26.327A3.441,3.441,0,0,0,3.14,30H26.86A3.44,3.44,0,0,0,30,26.327V3.672A3.44,3.44,0,0,0,26.86,0M2.093,26.327V3.672c0-.841.49-1.58,1.047-1.58H26.86c.558,0,1.047.74,1.047,1.58v17.1l-4.763-5.892a4.721,4.721,0,0,0-7.585.326l-8.573,12.7H3.14c-.556,0-1.047-.74-1.047-1.58m24.767,1.58H9.509l7.783-11.526a2.63,2.63,0,0,1,4.224-.183l6.372,7.882.019.016v2.231c0,.841-.488,1.58-1.047,1.58' transform='translate(0 0)' fill='%23b1b1b1'/%3E%3Cpath d='M7.571,11.139a4.019,4.019,0,1,0-4.019-4.02,4.022,4.022,0,0,0,4.019,4.02' transform='translate(2.643 2.308)' fill='%23b1b1b1'/%3E%3C/g%3E%3C/svg%3E%0A");
			background-size: 30px 30px;
		}

		&:after {
			display: none;
			background-image: var(--image-url-loaded);
			background-size: cover;
		}
	}

	&.multiple:deep(.dz-message):before {
		background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' width='30' height='29.924' viewBox='0 0 30 29.924'%3E%3Cdefs%3E%3CclipPath id='clip-path'%3E%3Crect id='Rettangolo_673' data-name='Rettangolo 673' width='30' height='29.924' fill='%23b1b1b1'/%3E%3C/clipPath%3E%3C/defs%3E%3Cg clip-path='url(%23clip-path)'%3E%3Cpath d='M25.69,26.724H4.8A3.158,3.158,0,0,1,1.9,23.359V3.367A3.159,3.159,0,0,1,4.8,0h20.9a3.16,3.16,0,0,1,2.892,3.367V23.359a3.159,3.159,0,0,1-2.892,3.365M4.8,2.095c-.378,0-.8.522-.8,1.272V23.359c0,.727.419,1.269.8,1.269h20.9c.377,0,.8-.542.8-1.269V3.367c0-.75-.419-1.272-.8-1.272Z' transform='translate(1.418 0)' fill='%23b1b1b1'/%3E%3Cpath d='M6.262,21.687a1.031,1.031,0,0,1-.584-.18A1.05,1.05,0,0,1,5.4,20.051l7.77-11.528a4.281,4.281,0,0,1,6.884-.3l5.615,6.954A1.047,1.047,0,1,1,24.037,16.5L18.423,9.543a2.131,2.131,0,0,0-1.8-.812A2.179,2.179,0,0,0,14.9,9.7L7.131,21.225a1.045,1.045,0,0,1-.869.461' transform='translate(3.882 4.956)' fill='%23b1b1b1'/%3E%3Cpath d='M8.7,10.075A3.671,3.671,0,1,1,12.366,6.4,3.672,3.672,0,0,1,8.7,10.075' transform='translate(3.745 2.042)' fill='%23b1b1b1'/%3E%3Cpath d='M23.789,28.555H2.892A3.158,3.158,0,0,1,0,25.19V5.2A3.16,3.16,0,0,1,2.892,1.831a1.047,1.047,0,0,1,0,2.095c-.377,0-.8.545-.8,1.272V25.19c0,.727.42,1.27.8,1.27h20.9c.377,0,.8-.543.8-1.27a1.047,1.047,0,1,1,2.093,0,3.157,3.157,0,0,1-2.89,3.365' transform='translate(0 1.368)' fill='%23b1b1b1'/%3E%3C/g%3E%3C/svg%3E%0A");
	}

	&:not(.multiple) {
		.dropzone-wrapper.filled .dropzone:deep(.dz-message), .dz-started:deep(.dz-message) {
			border: 1px solid $primary;

			&:after {
				display: block;
			}

			&:before {
				display: none;
			}
		}

		.dropzone-wrapper.filled .dropzone, .dz-started {
			pointer-events: none;
		}

		.dropzone-wrapper.filled .dropzone .delete-btn, .dz-started .delete-btn {
			pointer-events: all;
		}

		.dropzone-wrapper.filled {
			pointer-events: all;
		}

		.dropzone-wrapper.filled:hover .dropzone .delete-btn {
			display: block;
		}
		@media (max-width: 1080px) {
			.dropzone-wrapper.filled .dropzone .delete-btn {
				display: block;
			}
		}

		.summary-row {
			display: none;
		}
	}

	&.disabled:not(.multiple) .dropzone-wrapper.filled .dropzone .delete-btn, .dz-started .delete-btn {
		pointer-events: none;
	}

}
</style>